// src/services/pftTests.js
//
// A patient's PFT history: several dated tests instead of one set of fields.
// Firebase-free so every rule here is unit tested (pftTests.test.js).
//
// STORAGE (nothing existing is migrated or rewritten):
//   * The 9 flat fields every patient already has (pftDate, fev1fvcPre, ...) stay
//     exactly where they are, and keep meaning "the patient's PFT". Eligibility
//     matching (eligibilityMatch.js) and the query page read them unchanged.
//   * New: `pftTests`, a list of dated tests. It is written ONLY once a record
//     really has more than its old single PFT (a second date, a PFT from an
//     upload, or a value the flat fields can't hold). Until then a record keeps
//     its exact current shape.
//   * The flat fields always mirror the NEWEST test. A patient's existing values
//     become their first test ("legacy"), so they stay visible and are never
//     lost: adding an older test changes nothing, adding a newer one keeps the
//     old values as their own dated entry.
//   * `pftNA` is the record-level "N/A" toggle and is not part of a test.
//   * Tests are never deleted: "remove" sets `removedAt`, which hides the test
//     and leaves it on the record.

import { normalizeDob } from "./patientDuplicateMatch";
import { mergeSoftChecks } from "./pftSoftChecks";

// The flat fields, as stored today (test key -> record key). A test's `date` is
// the record's `pftDate`; every other key is the same name.
export const PFT_FLAT_KEYS = [
  "pftDate",
  "fev1fvcPre",
  "fev1fvcPost",
  "fev1Pre",
  "fev1Post",
  "fvcPre",
  "fvcPost",
  "revPercent",
  "revMl",
];
const flatToTestKey = (k) => (k === "pftDate" ? "date" : k);

// Field-list helpers: a value read pre and post bronchodilator, and a measurement
// with predicted, % predicted and unit ("tlc", "tlcPredicted", "tlcPct", "tlcUnit").
const prePost = (key, label) => [
  { key: `${key}Pre`, label: `${label}, Pre` },
  { key: `${key}Post`, label: `${label}, Post` },
];
const metric = (key, label) => [
  { key, label },
  { key: `${key}Predicted`, label: `${label} Predicted` },
  { key: `${key}Pct`, label: `${label} % Predicted` },
  { key: `${key}Unit`, label: `${label} unit` },
];
const METRIC_LABELS = {
  tlc: "TLC", rv: "RV", frc: "FRC", vc: "VC / SVC", ic: "IC", erv: "ERV",
  dlco: "DLCO", dlcoCorrected: "DLCO (Hb-corrected)", va: "VA", dlcoVa: "DLCO/VA (KCO)",
};
function metricRow(key) {
  return { label: METRIC_LABELS[key], actual: key, predicted: `${key}Predicted`, pct: `${key}Pct`, unit: `${key}Unit` };
}

// Everything a test can hold. The first block is the flat fields; the rest are
// new (what the PFT extraction reads that the portal had no field for).
export const PFT_TEST_FIELDS = [
  { key: "date", label: "PFT Date", type: "date" },
  { key: "fev1fvcPre", label: "FEV1/FVC Ratio (Actual), Pre" },
  { key: "fev1fvcPost", label: "FEV1/FVC Ratio (Actual), Post" },
  { key: "fev1Pre", label: "FEV1 % Predicted, Pre" },
  { key: "fev1Post", label: "FEV1 % Predicted, Post" },
  { key: "fvcPre", label: "FVC % Predicted, Pre" },
  { key: "fvcPost", label: "FVC % Predicted, Post" },
  { key: "revPercent", label: "FEV1 Reversibility, % change" },
  { key: "revMl", label: "FEV1 Reversibility, ml" },
  // New fields, grouped for display (PFT_FIELD_GROUPS below).
  ...prePost("fev1Actual", "FEV1 (L)"),
  ...prePost("fev1Predicted", "FEV1 Predicted (L)"),
  ...prePost("fvcActual", "FVC (L)"),
  ...prePost("fvcPredicted", "FVC Predicted (L)"),
  ...prePost("fev1Lln", "FEV1 LLN"),
  ...prePost("fev1Z", "FEV1 z-score"),
  ...prePost("fvcLln", "FVC LLN"),
  ...prePost("fvcZ", "FVC z-score"),
  ...prePost("fev1fvcLln", "FEV1/FVC LLN"),
  ...prePost("fev1fvcZ", "FEV1/FVC z-score"),
  ...prePost("fef2575", "FEF25-75"),
  ...prePost("fef2575Predicted", "FEF25-75 Predicted"),
  ...prePost("fef2575Pct", "FEF25-75 % Predicted"),
  ...prePost("pef", "PEF"),
  ...prePost("pefPredicted", "PEF Predicted"),
  ...prePost("pefPct", "PEF % Predicted"),
  { key: "fef2575Unit", label: "FEF25-75 unit" },
  { key: "pefUnit", label: "PEF unit" },
  ...metric("tlc", "TLC"),
  ...metric("rv", "RV"),
  { key: "rvTlc", label: "RV/TLC (%)" },
  ...metric("frc", "FRC"),
  ...metric("vc", "VC / SVC"),
  ...metric("ic", "IC"),
  ...metric("erv", "ERV"),
  ...metric("dlco", "DLCO"),
  ...metric("dlcoCorrected", "DLCO (Hb-corrected)"),
  { key: "hemoglobin", label: "Hemoglobin" },
  { key: "hemoglobinUnit", label: "Hemoglobin unit" },
  ...metric("va", "VA"),
  ...metric("dlcoVa", "DLCO/VA (KCO)"),
  { key: "methacholinePc20", label: "Methacholine PC20" },
  { key: "methacholinePd20", label: "Methacholine PD20" },
  { key: "methacholineUnit", label: "Methacholine unit" },
  { key: "fenoPpb", label: "FeNO (ppb)" },
  { key: "spo2", label: "SpO2 (%)" },
  { key: "sixMwtDistance", label: "6-minute walk distance" },
  { key: "sixMwtUnit", label: "6-minute walk unit" },
  { key: "height", label: "Height" },
  { key: "heightUnit", label: "Height unit" },
  { key: "weight", label: "Weight" },
  { key: "weightUnit", label: "Weight unit" },
  { key: "referringPhysician", label: "Referring physician" },
  { key: "interpretingPhysician", label: "Interpreting physician" },
  { key: "smokingStatus", label: "Smoking status" },
  { key: "packYears", label: "Pack-years" },
  { key: "bronchodilator", label: "Bronchodilator" },
  { key: "qualityGrade", label: "Test quality grade" },
  { key: "referenceEquations", label: "Reference equations" },
  { key: "interpretation", label: "Interpretation", type: "text" },
];
export const PFT_TEST_VALUE_KEYS = PFT_TEST_FIELDS.map((f) => f.key);

// What the patient page SHOWS beyond the first nine values: only the fields most PFT
// reports print (decided 2026-10-05 from a 20-report sample: each is on at least half
// of them). Every other value the extraction reads (LLN, z-scores, VC, IC, ERV,
// corrected DLCO, methacholine, FeNO, ...) is still SAVED on the test, and the full
// OCR text of every PDF is kept, so showing more later is only a change here.
// Row kinds:
//   pair   [label, preKey, postKey]
//   metric { label, actual, predicted, pct, unit }
//   single [key, unitKey?]
export const PFT_FIELD_GROUPS = [
  {
    title: "Spirometry volumes",
    pairs: [
      ["FEV1 (L)", "fev1ActualPre", "fev1ActualPost"],
      ["FEV1 Predicted (L)", "fev1PredictedPre", "fev1PredictedPost"],
      ["FVC (L)", "fvcActualPre", "fvcActualPost"],
      ["FVC Predicted (L)", "fvcPredictedPre", "fvcPredictedPost"],
    ],
  },
  {
    title: "Flow",
    pairs: [
      ["FEF25-75", "fef2575Pre", "fef2575Post"],
      ["FEF25-75 Predicted", "fef2575PredictedPre", "fef2575PredictedPost"],
      ["FEF25-75 % Predicted", "fef2575PctPre", "fef2575PctPost"],
      ["PEF", "pefPre", "pefPost"],
      ["PEF Predicted", "pefPredictedPre", "pefPredictedPost"],
      ["PEF % Predicted", "pefPctPre", "pefPctPost"],
    ],
    singles: [["fef2575Unit"], ["pefUnit"]],
  },
  {
    title: "Lung volumes",
    metrics: ["tlc", "rv", "frc"].map(metricRow),
    singles: [["rvTlc"]],
  },
  {
    title: "Diffusion",
    metrics: ["dlco", "va", "dlcoVa"].map(metricRow),
  },
  {
    title: "Test details",
    singles: [["height", "heightUnit"], ["weight", "weightUnit"], ["referringPhysician"]],
  },
];
// Descriptive text from the report, shown as a read-only note on the PFT (not boxes).
// The two extraction readings word these differently, so they are not compared: the
// first reading's text is kept. The PDF link is always there to check against.
// (Smoking status is NOT here: it is its own patient field, `smokingStatus` on the
// patient record, filled from the newest PFT through the merge.)
export const PFT_NOTE_FIELDS = ["interpretation", "bronchodilator", "qualityGrade"];
// Every key shown in the groups (to decide whether "More values" starts open).
export const PFT_GROUPED_KEYS = PFT_FIELD_GROUPS.flatMap((g) => [
  ...(g.pairs || []).flatMap(([, a, b]) => [a, b]),
  ...(g.metrics || []).flatMap((m) => [m.actual, m.predicted, m.pct, m.unit]),
  ...(g.singles || []).flat(),
]);
// Values this test HAS that aren't in the groups above (e.g. LLN, z-scores, FeNO): shown
// under "Other values from this report", so nothing extracted is out of sight. Units go
// with their value. A value left blank because the extraction's readings disagreed is
// listed too (with its note), and so is any key in `alsoShow` (e.g. one under review on
// the PFT Uploads page), so a box someone must check is never out of sight.
export const otherValueKeys = (test, alsoShow = []) => {
  const shown = new Set([
    ...PFT_FLAT_KEYS.map(flatToTestKey),
    ...PFT_GROUPED_KEYS,
    ...PFT_NOTE_FIELDS,
    "smokingStatus", // shown on the patient's profile
  ]);
  const has = (k) => test && test[k] !== undefined && test[k] !== null && String(test[k]).trim() !== "";
  const wanted = new Set([...Object.keys((test && test.disagreed) || {}), ...alsoShow]);
  return PFT_TEST_FIELDS.map((f) => f.key).filter((k) => !shown.has(k) && (has(k) || wanted.has(k)));
};
const LEGACY_VALUE_KEYS = PFT_FLAT_KEYS.map(flatToTestKey);

// Bookkeeping on a test (never compared as a "value").
// id, source ("manual" | "pft-upload" | "legacy"), importId, file, fileName,
// addedAt, addedBy, removedAt, removedBy.
export const LEGACY_TEST_ID = "legacy";

const isBlank = (v) => v === undefined || v === null || String(v).trim() === "";
const clean = (v) => (v === undefined || v === null ? "" : v);

/** The record's existing single PFT as a test, or null when it has none. */
export const legacyTestFromFlat = (record = {}) => {
  const test = { id: LEGACY_TEST_ID, source: "legacy" };
  let any = false;
  for (const k of PFT_FLAT_KEYS) {
    const v = clean(record[k]);
    test[flatToTestKey(k)] = v;
    if (!isBlank(v)) any = true;
  }
  return any ? test : null;
};

/**
 * Every test on a record. A record that has `pftTests` uses it; one that
 * doesn't shows its existing flat values as its only test.
 */
export const testsFromRecord = (record = {}) => {
  if (Array.isArray(record.pftTests)) return record.pftTests.filter(Boolean).map((t) => ({ ...t }));
  const legacy = legacyTestFromFlat(record);
  return legacy ? [legacy] : [];
};

export const isRemoved = (t) => !!(t && t.removedAt);

/** Sort key for a test date: "YYYY-MM-DD", or "" when unreadable / empty. */
export const testDateKey = (t) => normalizeDob(t && t.date);

/**
 * Visible tests, newest first. A test with no readable date sorts after every
 * dated one. Ties keep their order in the list (later-added first).
 */
export const visibleTestsNewestFirst = (tests = []) =>
  tests
    .map((t, i) => ({ t, i }))
    .filter(({ t }) => !isRemoved(t))
    .sort((a, b) => {
      const da = testDateKey(a.t);
      const db = testDateKey(b.t);
      if (da !== db) {
        if (!da) return 1;
        if (!db) return -1;
        return da < db ? 1 : -1;
      }
      return b.i - a.i;
    })
    .map(({ t }) => t);

export const newestTest = (tests = []) => visibleTestsNewestFirst(tests)[0] || null;

/**
 * The 9 flat fields for a list of tests: the newest visible test's values, or
 * all blank when there is none (only after a person removed every test).
 */
export const flatMirror = (tests = []) => {
  const newest = newestTest(tests);
  const out = {};
  for (const k of PFT_FLAT_KEYS) out[k] = newest ? clean(newest[flatToTestKey(k)]) : "";
  return out;
};

const sameValue = (a, b) => String(clean(a)).trim() === String(clean(b)).trim();

/**
 * Merge three copies of the list, by test id, so nothing is lost:
 *   base   what the page loaded
 *   fresh  what is stored now (a merge or another person may have changed it)
 *   local  what the page is about to save
 * Per field: a value this page changed (local != base) wins; otherwise the
 * stored value wins, so a change made elsewhere since loading survives. A test
 * found on only one side is kept. Tests are never dropped.
 */
export const mergeTests = (base = [], fresh = [], local = []) => {
  const byId = (list) => new Map(list.filter((t) => t && t.id).map((t) => [t.id, t]));
  const b = byId(base);
  const l = byId(local);
  const out = [];
  const seen = new Set();
  for (const f of fresh.filter((t) => t && t.id)) {
    seen.add(f.id);
    const lt = l.get(f.id);
    if (!lt) {
      out.push({ ...f });
      continue;
    }
    const bt = b.get(f.id) || {};
    const merged = { ...f };
    for (const key of new Set([...Object.keys(f), ...Object.keys(lt)])) {
      // Soft checks are an object (with a "Verified" tick): compared per check, since the
      // plain comparison below sees every object as the same (2026-10-20).
      if (key === "softChecks") {
        const sc = mergeSoftChecks(bt.softChecks, f.softChecks, lt.softChecks);
        if (Object.keys(sc).length) merged.softChecks = sc;
        else delete merged.softChecks;
        continue;
      }
      if (!sameValue(lt[key], bt[key])) merged[key] = lt[key];
    }
    out.push(merged);
  }
  for (const t of local) {
    if (t && t.id && !seen.has(t.id)) {
      seen.add(t.id);
      out.push({ ...t });
    }
  }
  return out;
};

/**
 * Does this list need `pftTests` on the record? Not while it is only the
 * record's old single PFT (with values the flat fields can hold).
 */
export const needsTestList = (tests = []) =>
  tests.some(
    (t) =>
      t.id !== LEGACY_TEST_ID ||
      isRemoved(t) ||
      PFT_TEST_VALUE_KEYS.some((k) => !LEGACY_VALUE_KEYS.includes(k) && !isBlank(t[k])),
  );

/**
 * The PFT part of a save. Returns the fields to write:
 *   * the 9 flat fields (always: the save path already writes them today), set
 *     to the newest test's values
 *   * `pftTests`, only when the stored record already has it or the list needs it
 * Arguments:
 *   stored  the record as stored right now (re-read just before saving)
 *   base    the tests the page loaded (testsFromRecord of the loaded record)
 *   local   the tests as edited on the page
 */
export const pftFieldsForSave = ({ stored = {}, base = [], local = [] }) => {
  const storedHasList = Array.isArray(stored.pftTests);
  const fresh = testsFromRecord(stored);
  const tests = mergeTests(base, fresh, local);
  if (!storedHasList && !needsTestList(tests)) {
    return flatMirror(tests);
  }
  return { ...flatMirror(tests), pftTests: tests };
};

/** A new, empty manual test. `id` and `now` are passed in to keep this pure. */
export const newManualTest = ({ id, now, by }) => {
  const t = { id, source: "manual", addedAt: now, addedBy: by || "" };
  for (const k of PFT_TEST_VALUE_KEYS) t[k] = "";
  return t;
};
